import { View, Text, StyleSheet } from 'react-native';
import { router, useLocalSearchParams } from 'expo-router';
import { useUnistyles } from 'react-native-unistyles';
import Unicon from '@/components/General/Unicon';
import usePlatform from '@/utils/hooks/usePlatform';
import useEmailEditor from '@/components/Email/useEmailEditor';
import TiptapEditor from '@/components/General/inputs/QuillEditor';
import TextBlock from '@/components/Email/blocks/TextBlock';
import ImageBlock from '@/components/Email/blocks/ImageBlock';
import ButtonBlock from '@/components/Email/blocks/ButtonBlock';
import DividerBlock from '@/components/Email/blocks/DividerBlock';
import SpacerBlock from '@/components/Email/blocks/SpacerBlock';
import HtmlBlock from '@/components/Email/blocks/HtmlBlock';
import SolidButton from '@/components/General/SolidButton';
import IconButton from '@/components/General/IconButton';
import DropdownMenu from '@/components/General/DropdownMenu';
import SchedulePicker, { getScheduledDate } from '@/components/General/SchedulePicker';
import Toast from 'react-native-toast-message';
import { sendEmail, sendTestEmail } from '@/data/emails';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { useQueryClient } from '@tanstack/react-query';
import { Mutation } from '@/utils/react-query/Mutations';
import { showAlert } from '@/utils/showAlert';
import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import {
    DndContext,
    DragOverlay,
    PointerSensor,
    useSensor,
    useSensors,
    closestCenter,
} from '@dnd-kit/core';
import {
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const BLOCK_TYPES = [
    { type: 'text', label: 'Text', icon: 'text' },
    { type: 'image', label: 'Image', icon: 'image-outline' },
    { type: 'button', label: 'Button', icon: 'tablet-landscape-outline' },
    { type: 'divider', label: 'Divider', icon: 'arrows-down-to-line', library: 'fa' },
    { type: 'spacer', label: 'Spacer', icon: 'arrows-alt-v', library: 'fa5' },
    { type: 'html', label: 'HTML', icon: 'code' },
];

const TEMPLATE_SORT_OPTIONS = [
    { key: 'updated-desc', name: 'Recently Updated', icon: { web: 'time-outline', ios: 'clock', android: 'schedule' } },
    { key: 'updated-asc', name: 'Oldest First', icon: { web: 'calendar-outline', ios: 'calendar', android: 'event' } },
    { key: 'name-asc', name: 'Name A-Z', icon: { web: 'arrow-up-outline', ios: 'arrow.up', android: 'arrow_upward' } },
    { key: 'name-desc', name: 'Name Z-A', icon: { web: 'arrow-down-outline', ios: 'arrow.down', android: 'arrow_downward' } },
];

const VARIABLES = [
    { key: 'firstName', label: 'First Name' },
    { key: 'lastName', label: 'Last Name' },
    { key: 'fullName', label: 'Full Name' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'organizationName', label: 'Organization Name' },
];

// ─── Modern Color Input ───────────────────────────────────────────────────────
function ColorInput({ value, onChange, colors, fonts }) {
    const v = value || '#ffffff';
    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            backgroundColor: colors.background,
            border: `1px solid ${colors.border}`,
            borderRadius: 8,
            padding: '6px 10px 6px 6px',
        }}>
            <label style={{
                width: 28,
                height: 28,
                borderRadius: 6,
                backgroundColor: v,
                border: `1px solid ${colors.border}`,
                cursor: 'pointer',
                flexShrink: 0,
                boxShadow: 'inset 0 0 0 2px rgba(255,255,255,0.4)',
                position: 'relative',
                overflow: 'hidden',
            }}>
                <input type="color" value={v} onChange={(e) => onChange(e.target.value)} style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }} />
            </label>
            <input
                type="text"
                value={v}
                onChange={(e) => onChange(e.target.value)}
                style={{
                    flex: 1,
                    border: 'none',
                    outline: 'none',
                    backgroundColor: 'transparent',
                    color: colors.text,
                    fontSize: 13,
                    fontFamily: fonts.body,
                    fontVariantNumeric: 'tabular-nums',
                    minWidth: 0,
                }}
            />
        </div>
    );
}

// ─── Sortable Block Wrapper ───────────────────────────────────────────────────
function SortableBlock({ block, isSelected, onSelect, onUpdate, onDelete, onDuplicate }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
    const { theme: { colors } } = useUnistyles();

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0 : 1,
        position: 'relative',
    };

    const renderBlock = () => {
        const props = { block, isSelected, onSelect, onUpdate, editMode: false };
        switch (block.type) {
            case 'text':     return <TextBlock {...props} />;
            case 'image':    return <ImageBlock {...props} />;
            case 'button':   return <ButtonBlock {...props} />;
            case 'divider':  return <DividerBlock {...props} />;
            case 'spacer':   return <SpacerBlock {...props} />;
            case 'html':     return <HtmlBlock {...props} />;
            default:         return null;
        }
    };

    return (
        <div ref={setNodeRef} style={style} onClick={(e) => e.stopPropagation()}>
            {isSelected && (
                <div style={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    display: 'flex',
                    gap: 4,
                    zIndex: 20,
                    backgroundColor: colors.primary,
                    borderBottomLeftRadius: 6,
                    padding: '4px 6px',
                }}>
                    <button
                        {...attributes}
                        {...listeners}
                        title="Drag to reorder"
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'grab',
                            color: '#fff',
                            padding: '4px 6px',
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: 16,
                        }}
                    >
                        <Unicon name="drag-vertical" library="material" size={16} colorVariant="onPrimary" />
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); onDuplicate(block.id); }}
                        title="Duplicate"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#fff', padding: '4px 6px', fontSize: 16 }}
                    >
                        <Unicon name="content-copy" library="material" size={16} colorVariant="onPrimary" />
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); onDelete(); }}
                        title="Delete"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#fff', padding: '4px 6px', fontSize: 16 }}
                    >
                        <Unicon name="close" size={16} colorVariant="onPrimary" />
                    </button>
                </div>
            )}
            {renderBlock()}
        </div>
    );
}

// ─── Text Content Editor with Variable Insert ─────────────────────────────────
function TextContentEditor({ value, onChange, colors, fonts }) {
    const editorRef = useRef(null);
    const [varMenuOpen, setVarMenuOpen] = useState(false);

    const insertVariable = (key) => {
        const editor = editorRef.current?.getEditor?.();
        if (editor) {
            editor.chain().focus().insertContent(`{{${key}}}`).run();
        } else {
            onChange((value || '') + `{{${key}}}`);
        }
        setVarMenuOpen(false);
    };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8, position: 'relative' }}>
                <button
                    type="button"
                    onClick={() => setVarMenuOpen((v) => !v)}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '6px 12px',
                        fontSize: 12,
                        fontWeight: '500',
                        fontFamily: fonts.body,
                        backgroundColor: colors.background,
                        color: colors.text,
                        border: `1px solid ${colors.border}`,
                        borderRadius: 6,
                        cursor: 'pointer',
                    }}
                >
                    <span style={{ fontFamily: 'monospace', fontSize: 13 }}>{'{ }'}</span>
                    Insert Variable
                </button>
                {varMenuOpen && (
                    <div
                        style={{
                            position: 'absolute',
                            top: '100%',
                            right: 0,
                            marginTop: 4,
                            backgroundColor: colors.surface,
                            border: `1px solid ${colors.border}`,
                            borderRadius: 8,
                            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                            zIndex: 50,
                            minWidth: 200,
                            overflow: 'hidden',
                        }}
                    >
                        {VARIABLES.map((v) => (
                            <button
                                key={v.key}
                                type="button"
                                onClick={() => insertVariable(v.key)}
                                style={{
                                    display: 'flex',
                                    width: '100%',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '10px 14px',
                                    fontSize: 13,
                                    fontFamily: fonts.body,
                                    backgroundColor: 'transparent',
                                    color: colors.text,
                                    border: 'none',
                                    cursor: 'pointer',
                                    textAlign: 'left',
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = colors.background; }}
                                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                            >
                                <span>{v.label}</span>
                                <span style={{ fontFamily: 'monospace', fontSize: 11, color: colors.textTertiary }}>{`{{${v.key}}}`}</span>
                            </button>
                        ))}
                    </div>
                )}
            </div>
            <TiptapEditor ref={editorRef} value={value} onChange={onChange} />
        </div>
    );
}

// ─── Block Settings Panel (right sidebar content) ────────────────────────────
function BlockSettingsPanel({ block, onUpdateStyles, onUpdateContent, colors, fonts }) {
    if (!block) {
        return (
            <div style={{ padding: 24 }}>
                <p style={{ fontSize: 13, color: colors.textTertiary, fontFamily: fonts.body }}>
                    Select a block to edit its settings.
                </p>
            </div>
        );
    }

    const blockType = BLOCK_TYPES.find((bt) => bt.type === block.type);

    const inputStyle = {
        width: '100%',
        padding: '9px 12px',
        backgroundColor: colors.background,
        border: `1px solid ${colors.border}`,
        borderRadius: 8,
        color: colors.text,
        fontSize: 13,
        fontFamily: fonts.body,
        outline: 'none',
        boxSizing: 'border-box',
    };

    const inputLabelStyle = {
        display: 'block',
        fontSize: 10,
        fontWeight: '600',
        color: colors.textTertiary,
        marginBottom: 6,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        fontFamily: fonts.body,
    };

    const sectionTitleStyle = {
        fontSize: 14,
        fontWeight: '700',
        color: colors.text,
        fontFamily: fonts.heading,
        marginBottom: 14,
        paddingBottom: 8,
        borderBottom: `1px solid ${colors.border}`,
    };

    const sectionStyle = { marginBottom: 28 };
    const row2 = { display: 'flex', gap: 12, marginBottom: 12 };

    return (
        <div style={{ padding: '20px 24px 32px', overflowY: 'auto', height: '100%' }}>

            {/* Block type header with icon */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                marginBottom: 24,
                padding: '14px 16px',
                backgroundColor: colors.background,
                borderRadius: 12,
                border: `1px solid ${colors.border}`,
            }}>
                <div style={{
                    width: 42,
                    height: 42,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: colors.primaryContainer || colors.surface,
                    borderRadius: 10,
                    flexShrink: 0,
                }}>
                    <Unicon name={blockType?.icon || 'cube'} library={blockType?.library ?? 'ionicon'} size={22} colorVariant="primary" />
                </div>
                <div>
                    <div style={{ fontSize: 16, fontWeight: '700', color: colors.text, fontFamily: fonts.heading }}>
                        {blockType?.label || block.type}
                    </div>
                    <div style={{ fontSize: 12, color: colors.textTertiary, fontFamily: fonts.body, marginTop: 2 }}>
                        Block settings
                    </div>
                </div>
            </div>

            {/* Spacing */}
            <div style={sectionStyle}>
                <h3 style={sectionTitleStyle}>Spacing</h3>
                <label style={inputLabelStyle}>Padding</label>
                <div style={row2}>
                    <div style={{ flex: 1 }}>
                        <label style={inputLabelStyle}>Top</label>
                        <input type="number" min="0" value={block.styles.paddingTop ?? 10} onChange={(e) => onUpdateStyles({ paddingTop: Number(e.target.value) })} style={inputStyle} />
                    </div>
                    <div style={{ flex: 1 }}>
                        <label style={inputLabelStyle}>Right</label>
                        <input type="number" min="0" value={block.styles.paddingRight ?? 20} onChange={(e) => onUpdateStyles({ paddingRight: Number(e.target.value) })} style={inputStyle} />
                    </div>
                </div>
                <div style={row2}>
                    <div style={{ flex: 1 }}>
                        <label style={inputLabelStyle}>Bottom</label>
                        <input type="number" min="0" value={block.styles.paddingBottom ?? 10} onChange={(e) => onUpdateStyles({ paddingBottom: Number(e.target.value) })} style={inputStyle} />
                    </div>
                    <div style={{ flex: 1 }}>
                        <label style={inputLabelStyle}>Left</label>
                        <input type="number" min="0" value={block.styles.paddingLeft ?? 20} onChange={(e) => onUpdateStyles({ paddingLeft: Number(e.target.value) })} style={inputStyle} />
                    </div>
                </div>
            </div>

            {/* Appearance */}
            <div style={sectionStyle}>
                <h3 style={sectionTitleStyle}>Appearance</h3>
                <div style={{ marginBottom: 14 }}>
                    <label style={inputLabelStyle}>Background Color</label>
                    <ColorInput value={block.styles.backgroundColor || '#ffffff'} onChange={(c) => onUpdateStyles({ backgroundColor: c })} colors={colors} fonts={fonts} />
                </div>

                {/* Alignment with icons */}
                {['text', 'button', 'image'].includes(block.type) && (
                    <div>
                        <label style={inputLabelStyle}>Text Align</label>
                        <div style={{ display: 'flex', gap: 8 }}>
                            {[
                                { align: 'left', icon: 'align-left' },
                                { align: 'center', icon: 'align-center' },
                                { align: 'right', icon: 'align-right' },
                            ].map(({ align, icon }) => (
                                <button
                                    key={align}
                                    onClick={() => onUpdateStyles({ textAlign: align })}
                                    style={{
                                        flex: 1,
                                        padding: '10px',
                                        backgroundColor: block.styles.textAlign === align ? colors.primary : colors.background,
                                        color: block.styles.textAlign === align ? '#fff' : colors.text,
                                        border: `1px solid ${block.styles.textAlign === align ? colors.primary : colors.border}`,
                                        borderRadius: 8,
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: 'all 0.15s ease',
                                    }}
                                    title={align}
                                >
                                    <Unicon name={icon} library="fa" size={16} colorVariant={block.styles.textAlign === align ? 'onPrimary' : 'text'} />
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Block-specific */}
            {block.type === 'button' && (
                <div style={sectionStyle}>
                    <h3 style={sectionTitleStyle}>Button</h3>
                    <div style={{ marginBottom: 12 }}>
                        <label style={inputLabelStyle}>Label</label>
                        <input type="text" value={block.content.text || ''} onChange={(e) => onUpdateContent({ text: e.target.value })} style={inputStyle} />
                    </div>
                    <div style={{ marginBottom: 12 }}>
                        <label style={inputLabelStyle}>URL</label>
                        <input type="url" value={block.content.href || ''} onChange={(e) => onUpdateContent({ href: e.target.value })} style={inputStyle} />
                    </div>
                    <div style={row2}>
                        <div style={{ flex: 1 }}>
                            <label style={inputLabelStyle}>Background</label>
                            <ColorInput value={block.content.backgroundColor || '#1460a5'} onChange={(c) => onUpdateContent({ backgroundColor: c })} colors={colors} fonts={fonts} />
                        </div>
                        <div style={{ flex: 1 }}>
                            <label style={inputLabelStyle}>Text</label>
                            <ColorInput value={block.content.textColor || '#ffffff'} onChange={(c) => onUpdateContent({ textColor: c })} colors={colors} fonts={fonts} />
                        </div>
                    </div>
                    <div>
                        <label style={inputLabelStyle}>Border Radius</label>
                        <input type="number" min="0" max="50" value={block.content.borderRadius ?? 4} onChange={(e) => onUpdateContent({ borderRadius: Number(e.target.value) })} style={inputStyle} />
                    </div>
                </div>
            )}

            {block.type === 'image' && (
                <div style={sectionStyle}>
                    <h3 style={sectionTitleStyle}>Image</h3>
                    <div style={{ marginBottom: 12 }}>
                        <label style={inputLabelStyle}>Alt Text</label>
                        <input type="text" value={block.content.alt || ''} onChange={(e) => onUpdateContent({ alt: e.target.value })} style={inputStyle} />
                    </div>
                    <div style={{ marginBottom: 12 }}>
                        <label style={inputLabelStyle}>Link URL</label>
                        <input type="url" value={block.content.href || ''} onChange={(e) => onUpdateContent({ href: e.target.value })} style={inputStyle} />
                    </div>
                    <div>
                        <label style={inputLabelStyle}>Max Width</label>
                        <input type="text" value={block.content.width || '100%'} onChange={(e) => onUpdateContent({ width: e.target.value })} style={inputStyle} placeholder="100% or 400px" />
                    </div>
                </div>
            )}

            {block.type === 'divider' && (
                <div style={sectionStyle}>
                    <h3 style={sectionTitleStyle}>Divider</h3>
                    <div style={row2}>
                        <div style={{ flex: 1 }}>
                            <label style={inputLabelStyle}>Thickness</label>
                            <input type="number" min="1" value={block.content.thickness || 1} onChange={(e) => onUpdateContent({ thickness: Number(e.target.value) })} style={inputStyle} />
                        </div>
                        <div style={{ flex: 1 }}>
                            <label style={inputLabelStyle}>Color</label>
                            <ColorInput value={block.content.color || '#e5e7eb'} onChange={(c) => onUpdateContent({ color: c })} colors={colors} fonts={fonts} />
                        </div>
                    </div>
                </div>
            )}

            {block.type === 'spacer' && (
                <div style={sectionStyle}>
                    <h3 style={sectionTitleStyle}>Spacer</h3>
                    <label style={inputLabelStyle}>Height (px)</label>
                    <input type="number" min="4" max="200" value={block.content.height || 20} onChange={(e) => onUpdateContent({ height: Number(e.target.value) })} style={inputStyle} />
                </div>
            )}

            {/* Content editor for text/html blocks */}
            {block.type === 'text' && (
                <div style={sectionStyle}>
                    <h3 style={sectionTitleStyle}>Content</h3>
                    <TextContentEditor
                        value={block.content.html || ''}
                        onChange={(html) => onUpdateContent({ html })}
                        colors={colors}
                        fonts={fonts}
                    />
                </div>
            )}

            {block.type === 'html' && (
                <div style={sectionStyle}>
                    <h3 style={sectionTitleStyle}>Raw HTML</h3>
                    <textarea
                        value={block.content.rawHtml || ''}
                        onChange={(e) => onUpdateContent({ rawHtml: e.target.value })}
                        rows={18}
                        placeholder="Enter raw HTML..."
                        style={{ ...inputStyle, fontFamily: 'monospace', resize: 'vertical', minHeight: 300 }}
                    />
                </div>
            )}
        </div>
    );
}

// ─── Block Preview (used in DragOverlay) ──────────────────────────────────────
function BlockPreview({ block }) {
    const noop = () => {};
    const props = { block, isSelected: false, onSelect: noop, onUpdate: noop };
    switch (block.type) {
        case 'text':    return <TextBlock {...props} />;
        case 'image':   return <ImageBlock {...props} />;
        case 'button':  return <ButtonBlock {...props} />;
        case 'divider': return <DividerBlock {...props} />;
        case 'spacer':  return <SpacerBlock {...props} />;
        case 'html':    return <HtmlBlock {...props} />;
        default:        return null;
    }
}

// ─── Editor View (inside wrapper) ─────────────────────────────────────────────
function EmailEditorView({ initialTemplate, recipientIDs, onChangeTemplate }) {
    const { theme: { colors, fonts } } = useUnistyles();
    const { isWeb } = usePlatform();
    const saveTemplateMutation = Mutation.emailTemplates.save();
    const queryClient = useQueryClient();
    const { data: loggedInUser } = Query.people.loggedInUser();
    const [templateID, setTemplateID] = useState(initialTemplate?.id || null);
    const [templateName, setTemplateName] = useState(initialTemplate?.name || 'New Email Template');
    const [templateSubject, setTemplateSubject] = useState(initialTemplate?.subject || '');
    const [settingsTab, setSettingsTab] = useState('email'); // 'email' or 'block'
    const [sendModalOpen, setSendModalOpen] = useState(false);
    const [sending, setSending] = useState(false);
    const [sendingTest, setSendingTest] = useState(false);
    const [activeId, setActiveId] = useState(null);
    const [previewMode, setPreviewMode] = useState(false);

    const {
        design,
        selectedId,
        setSelectedId,
        addRow,
        addColumn,
        removeColumn,
        addBlock,
        removeBlock,
        moveBlock,
        updateBlockContent,
        updateBlockStyles,
        duplicateBlock,
        undo,
        redo,
        canUndo,
        canRedo,
        getSelectedBlock,
    } = useEmailEditor(initialTemplate?.design || null);

    const selectedBlock = getSelectedBlock();

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
    );

    const findBlockById = useCallback((id) => {
        if (!id) return null;
        for (const row of design.rows) {
            for (const col of row.columns) {
                const b = col.blocks.find(bl => bl.id === id);
                if (b) return b;
            }
        }
        return null;
    }, [design]);

    const handleDragStart = useCallback((event) => {
        setActiveId(event.active?.id ?? null);
    }, []);

    const handleDragEnd = useCallback((event) => {
        setActiveId(null);
        const { active, over } = event;
        if (!active || !over || active.id === over.id) return;

        for (const row of design.rows) {
            for (const col of row.columns) {
                const ids = col.blocks.map(b => b.id);
                if (ids.includes(active.id) && ids.includes(over.id)) {
                    const newIndex = ids.indexOf(over.id);
                    moveBlock(col.id, col.id, active.id, newIndex);
                    return;
                }
            }
        }
    }, [design, moveBlock]);

    const handleDragCancel = useCallback(() => setActiveId(null), []);

    // Auto-switch to Block Settings when a block is selected
    useEffect(() => {
        if (selectedId && findBlockById(selectedId)) {
            setSettingsTab('block');
        }
    }, [selectedId, findBlockById]);

    const activeBlock = activeId ? findBlockById(activeId) : null;

    const persistTemplate = async () => {
        const details = { name: templateName, subject: templateSubject, design };
        if (templateID) details.id = templateID;
        const saved = await saveTemplateMutation.mutateAsync({ details });
        if (saved?.id && !templateID) setTemplateID(saved.id);
        return saved;
    };

    const handleSave = async () => {
        try {
            await persistTemplate();
        } catch (e) {
            console.error(e);
            Toast.show({ type: 'error', text2: 'Failed to save template' });
        }
    };

    const persistAndGetTemplateID = async () => {
        const saved = await persistTemplate();
        const idToUse = saved?.id || templateID;
        if (!idToUse) throw new Error('Template save failed');
        return idToUse;
    };

    const handleSendPress = async () => {
        if (recipientIDs?.length > 0) {
            setSendModalOpen(true);
            return;
        }

        setSending(true);
        try {
            const idToSend = await persistAndGetTemplateID();
            router.push({
                pathname: '/people',
                params: { compose: 'email', templateID: idToSend },
            });
        } catch (e) {
            console.error(e);
            Toast.show({ type: 'error', text2: 'Save the template before selecting recipients' });
        } finally {
            setSending(false);
        }
    };

    const handleSendTest = async () => {
        setSendingTest(true);
        try {
            const idToSend = await persistAndGetTemplateID();
            await sendTestEmail({
                templateID: idToSend,
                subject: templateSubject?.trim() || templateName || 'Email template',
            });
        } catch (e) {
            console.error(e);
        } finally {
            setSendingTest(false);
        }
    };

    const confirmSendTest = () => {
        const targetEmail = loggedInUser?.accountEmail || loggedInUser?.email;
        if (!targetEmail) {
            Toast.show({ type: 'error', text2: 'Your account does not have an email address' });
            return;
        }
        showAlert({
            title: 'Send test email?',
            subtitle: `A test version will be sent to ${targetEmail}.`,
            confirmText: 'Send Test',
            onConfirm: handleSendTest,
        });
    };

    const handleSend = async ({ scheduleMode = 'now', scheduledFor = null } = {}) => {
        if (!recipientIDs || recipientIDs.length === 0) {
            Toast.show({ type: 'error', text2: 'No recipients selected' });
            return;
        }
        if (!templateSubject?.trim()) {
            Toast.show({ type: 'error', text2: 'Please enter a subject in Email Settings' });
            setSettingsTab('email');
            return;
        }
        const scheduledDate = getScheduledDate(scheduleMode, scheduledFor);
        if (scheduleMode === 'later' && !scheduledDate) {
            Toast.show({ type: 'error', text2: 'Choose a future date and time to send this email.' });
            return;
        }
        setSending(true);
        try {
            const idToSend = await persistAndGetTemplateID();
            await sendEmail({
                type: 'template',
                subject: templateSubject,
                recipientIDs,
                templateID: idToSend,
                scheduledFor: scheduledDate?.toISOString(),
            });
            queryClient.invalidateQueries({ queryKey: QueryKeys.emailCampaigns.all() });
            setSendModalOpen(false);
        } catch (e) {
            console.error(e);
        } finally {
            setSending(false);
        }
    };

    if (!isWeb) {
        return (
            <View style={[styles.container, { backgroundColor: colors.background }]}>
                <Text style={[styles.message, { color: colors.text }]}>The email editor is available on the web app only.</Text>
            </View>
        );
    }

    const btnBase = {
        padding: '8px 16px',
        borderRadius: 6,
        fontSize: 13,
        fontWeight: '500',
        fontFamily: fonts.body,
        cursor: 'pointer',
        border: 'none',
    };

    const inputStyle = {
        padding: '8px 12px',
        backgroundColor: colors.background,
        border: `1px solid ${colors.border}`,
        borderRadius: 6,
        color: colors.text,
        fontSize: 13,
        fontFamily: fonts.body,
        outline: 'none',
    };

    const labelStyle = {
        display: 'block',
        fontSize: 11,
        fontWeight: '600',
        color: colors.textSecondary,
        marginBottom: 6,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        fontFamily: fonts.body,
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: 'transparent' }}>

            {/* ── Header ── */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '0 20px',
                height: 56,
                backgroundColor: colors.surface,
                borderBottom: `1px solid ${colors.border}`,
                flexShrink: 0,
            }}>
                <button onClick={() => onChangeTemplate ? onChangeTemplate() : router.back()} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                    <Unicon name="chevron-back" size={24} colorVariant="primary" />
                </button>
                <span style={{ fontSize: 16, fontWeight: '600', color: colors.text, fontFamily: fonts.heading, flex: 1 }}>
                    {templateName || 'Email Editor'}
                </span>
                {!previewMode && (
                    <>
                        <IconButton
                            iconName="arrow-undo"
                            onPress={undo}
                            disabled={!canUndo}
                            tooltipText="Undo"
                            type="primary"
                            iconSize={20}
                        />
                        <IconButton
                            iconName="arrow-redo"
                            onPress={redo}
                            disabled={!canRedo}
                            tooltipText="Redo"
                            type="primary"
                            iconSize={20}
                        />
                    </>
                )}
                <SolidButton
                    variant="muted"
                    type="primary"
                    text={previewMode ? 'Exit Preview' : 'Preview'}
                    iconName="eye"
                    iconBefore
                    onPress={() => setPreviewMode(p => !p)}
                    size="sm"
                />
                <SolidButton
                    variant="muted"
                    type="secondary"
                    text="Send Test"
                    iconName="flask-outline"
                    iconBefore
                    onPress={confirmSendTest}
                    processing={sendingTest}
                    size="sm"
                    testID="SendTestEmailButton"
                />
                <SolidButton
                    variant="muted"
                    type="success"
                    text="Save"
                    onPress={handleSave}
                    size="sm"
                />
                <SolidButton
                    variant="muted"
                    type="primary"
                    text="Send"
                    iconName="paper-plane"
                    iconLibrary="fa"
                    iconBefore
                    onPress={handleSendPress}
                    processing={sending}
                    size="sm"
                    testID="SendTemplateEmailButton"
                />
            </div>

            {/* ── Body ── */}
            <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

                {/* ── Canvas ── */}
                <div
                    style={{ flex: 1, overflowY: 'auto', backgroundColor: colors.background, padding: '32px 32px 120px' }}
                    onClick={() => setSelectedId(null)}
                >
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>
                        <div style={{
                            maxWidth: design.globalStyles.contentWidth || 600,
                            margin: '0 auto',
                            backgroundColor: '#ffffff',
                            fontFamily: design.globalStyles.fontFamily || 'Arial, Helvetica, sans-serif',
                            boxShadow: '0 4px 24px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)',
                            borderRadius: 12,
                        }}>
                            {design.rows.length === 0 && !previewMode ? (
                                <div style={{
                                    padding: '80px 40px',
                                    textAlign: 'center',
                                    backgroundColor: '#ffffff',
                                }}>
                                    <p style={{ color: '#6b7280', marginBottom: 20, fontSize: 15, fontFamily: fonts.body }}>Your email is empty</p>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); addRow(); }}
                                        style={{ ...btnBase, backgroundColor: colors.primary, color: '#fff', padding: '12px 28px', fontSize: 14 }}
                                    >
                                        + Add First Row
                                    </button>
                                </div>
                            ) : (
                                design.rows.map((row, rowIndex) => {
                                    const rowContainsSelected =
                                        selectedId === row.id ||
                                        row.columns.some(c => c.blocks.some(b => b.id === selectedId));
                                    const rowSelected = !previewMode && rowContainsSelected;
                                    const isLastRow = rowIndex === design.rows.length - 1;
                                    return (
                                        <div
                                            key={row.id}
                                            onClick={(e) => { e.stopPropagation(); setSelectedId(row.id); }}
                                            style={{
                                                position: 'relative',
                                                padding: `${row.styles.paddingTop || 0}px ${row.styles.paddingRight || 0}px ${row.styles.paddingBottom || 0}px ${row.styles.paddingLeft || 0}px`,
                                                backgroundColor: row.styles.backgroundColor || '#ffffff',
                                                borderBottom: !previewMode && !isLastRow ? `1px dashed ${colors.border}` : 'none',
                                            }}
                                        >
                                            {/* Row selection outline overlay */}
                                            {rowSelected && (
                                                <div
                                                    style={{
                                                        position: 'absolute',
                                                        inset: 0,
                                                        border: `2px solid ${colors.secondary}`,
                                                        pointerEvents: 'none',
                                                        zIndex: 15,
                                                    }}
                                                />
                                            )}
                                            {/* Row toolbar (top-left) - only visible when row is selected */}
                                            {rowSelected && (
                                                <div
                                                    onClick={(e) => e.stopPropagation()}
                                                    style={{
                                                        position: 'absolute',
                                                        top: 2,
                                                        left: 2,
                                                        display: 'flex',
                                                        gap: 4,
                                                        zIndex: 20,
                                                        backgroundColor: colors.secondary,
                                                        borderBottomRightRadius: 6,
                                                        padding: '4px 6px',
                                                    }}
                                                >
                                                    {row.columns.length < 3 && (
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); addColumn(row.id); }}
                                                            title="Add Column"
                                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#fff', padding: '4px 6px', fontSize: 16, display: 'flex', alignItems: 'center' }}
                                                        >
                                                            <Unicon name="plus" library="material" size={16} colorVariant="onSecondary" />
                                                        </button>
                                                    )}
                                                    {row.columns.length > 1 && (
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); removeColumn(row.id, row.columns[row.columns.length - 1].id); }}
                                                            title="Remove Column"
                                                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#fff', padding: '4px 6px', fontSize: 16, display: 'flex', alignItems: 'center' }}
                                                        >
                                                            <Unicon name="remove" size={16} colorVariant="onSecondary" />
                                                        </button>
                                                    )}
                                                </div>
                                            )}

                                            {/* Columns */}
                                            <div style={{ display: 'flex', position: 'relative' }}>
                                                {row.columns.map((col) => (
                                                    <div
                                                        key={col.id}
                                                        style={{
                                                            flex: col.width / 100,
                                                            minWidth: 0,
                                                            borderRight: !previewMode && row.columns.indexOf(col) < row.columns.length - 1 ? `1px dashed ${colors.border}` : 'none',
                                                        }}
                                                    >
                                                        <SortableContext items={col.blocks.map(b => b.id)} strategy={verticalListSortingStrategy}>
                                                            {col.blocks.map((block) => (
                                                                previewMode ? (
                                                                    <div key={block.id}>
                                                                        <BlockPreview block={block} />
                                                                    </div>
                                                                ) : (
                                                                    <SortableBlock
                                                                        key={block.id}
                                                                        block={block}
                                                                        isSelected={selectedId === block.id}
                                                                        onSelect={setSelectedId}
                                                                        onUpdate={updateBlockContent}
                                                                        onDelete={() => removeBlock(col.id, block.id)}
                                                                        onDuplicate={duplicateBlock}
                                                                    />
                                                                )
                                                            ))}
                                                        </SortableContext>

                                                        {/* Element selector - always visible in edit mode */}
                                                        {!previewMode && (
                                                            <div style={{
                                                                padding: 12,
                                                                backgroundColor: colors.background,
                                                                border: `1px dashed ${colors.border}`,
                                                                borderRadius: 6,
                                                                margin: 12,
                                                            }}>
                                                                <div style={{
                                                                    fontSize: 11,
                                                                    fontWeight: '600',
                                                                    color: colors.textSecondary,
                                                                    fontFamily: fonts.body,
                                                                    textTransform: 'uppercase',
                                                                    letterSpacing: 0.5,
                                                                    marginBottom: 8,
                                                                    textAlign: 'center',
                                                                }}>
                                                                    Add an Element
                                                                </div>
                                                                <div style={{
                                                                    display: 'flex',
                                                                    gap: 6,
                                                                    justifyContent: 'center',
                                                                    flexWrap: 'wrap',
                                                                }}>
                                                                    {BLOCK_TYPES.map((bt) => (
                                                                        <button
                                                                            key={bt.type}
                                                                            onClick={(e) => { e.stopPropagation(); addBlock(col.id, bt.type); setSelectedId(null); }}
                                                                            title={bt.label}
                                                                            style={{
                                                                                display: 'flex',
                                                                                flexDirection: 'column',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                gap: 4,
                                                                                padding: '6px 8px',
                                                                                minWidth: 52,
                                                                                backgroundColor: colors.surface,
                                                                                border: `1px solid ${colors.border}`,
                                                                                borderRadius: 6,
                                                                                cursor: 'pointer',
                                                                                color: colors.textSecondary,
                                                                                transition: 'all 0.15s ease',
                                                                                fontFamily: fonts.body,
                                                                            }}
                                                                            onMouseEnter={(e) => {
                                                                                e.currentTarget.style.backgroundColor = colors.background;
                                                                                e.currentTarget.style.borderColor = colors.primary;
                                                                            }}
                                                                            onMouseLeave={(e) => {
                                                                                e.currentTarget.style.backgroundColor = colors.surface;
                                                                                e.currentTarget.style.borderColor = colors.border;
                                                                            }}
                                                                        >
                                                                            <Unicon name={bt.icon} library={bt.library ?? 'ionicon'} size={16} colorVariant="textSecondary" />
                                                                            <span style={{ fontSize: 10, color: colors.textSecondary }}>{bt.label}</span>
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })
                            )}

                        </div>

                        {/* Persistent Add Row button - hidden in preview mode */}
                        {!previewMode && design.rows.length > 0 && (
                            <div style={{ maxWidth: design.globalStyles.contentWidth || 600, margin: '12px auto 0' }}>
                                <button
                                    onClick={(e) => { e.stopPropagation(); addRow(); }}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 8,
                                        width: '100%',
                                        padding: '14px 0',
                                        fontSize: 14,
                                        fontWeight: '600',
                                        color: colors.primary,
                                        backgroundColor: 'transparent',
                                        border: `2px dashed ${colors.primary}`,
                                        borderRadius: 8,
                                        cursor: 'pointer',
                                        fontFamily: fonts.body,
                                    }}
                                >
                                    <Unicon name="plus" library="material" size={18} colorVariant="primary" />
                                    Add Row
                                </button>
                            </div>
                        )}

                        <DragOverlay dropAnimation={null} zIndex={1000}>
                            {activeBlock ? (
                                <div style={{
                                    backgroundColor: '#ffffff',
                                    boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
                                    borderRadius: 6,
                                    outline: `2px solid ${colors.primary}`,
                                    opacity: 0.95,
                                    pointerEvents: 'none',
                                }}>
                                    <BlockPreview block={activeBlock} />
                                </div>
                            ) : null}
                        </DragOverlay>
                    </DndContext>
                </div>

                {/* ── Right sidebar (wider) ── */}
                {!previewMode && (
                <div style={{
                    width: 480,
                    flexShrink: 0,
                    backgroundColor: colors.surface,
                    borderLeft: `1px solid ${colors.border}`,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}>
                    {/* Tabs */}
                    <div style={{ display: 'flex', gap: 4, borderBottom: `1px solid ${colors.border}`, flexShrink: 0 }}>
                        <button
                            onClick={() => setSettingsTab('email')}
                            style={{
                                flex: 1,
                                padding: '14px 0',
                                fontSize: 14,
                                fontWeight: '600',
                                backgroundColor: 'transparent',
                                color: settingsTab === 'email' ? colors.primary : colors.textSecondary,
                                border: 'none',
                                borderBottom: `2px solid ${settingsTab === 'email' ? colors.primary : 'transparent'}`,
                                cursor: 'pointer',
                                fontFamily: fonts.body,
                            }}
                        >
                            Email Settings
                        </button>
                        <button
                            onClick={() => setSettingsTab('block')}
                            style={{
                                flex: 1,
                                padding: '14px 0',
                                fontSize: 14,
                                fontWeight: '600',
                                backgroundColor: 'transparent',
                                color: settingsTab === 'block' ? colors.primary : colors.textSecondary,
                                border: 'none',
                                borderBottom: `2px solid ${settingsTab === 'block' ? colors.primary : 'transparent'}`,
                                cursor: 'pointer',
                                fontFamily: fonts.body,
                            }}
                        >
                            Block Settings
                        </button>
                    </div>

                    {/* Tab content */}
                    <div style={{ flex: 1, overflowY: 'auto' }}>
                        {settingsTab === 'email' ? (
                            <div style={{ padding: 20 }}>
                                <p style={{ fontSize: 12, fontWeight: '600', color: colors.textSecondary, marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: fonts.body }}>
                                    TEMPLATE
                                </p>
                                <div style={{ marginBottom: 16 }}>
                                    <label style={labelStyle}>Name</label>
                                    <input
                                        type="text"
                                        value={templateName}
                                        onChange={(e) => setTemplateName(e.target.value)}
                                        style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
                                    />
                                </div>
                                <div>
                                    <label style={labelStyle}>Subject Line</label>
                                    <input
                                        type="text"
                                        value={templateSubject}
                                        onChange={(e) => setTemplateSubject(e.target.value)}
                                        placeholder="Email subject..."
                                        style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
                                    />
                                </div>
                            </div>
                        ) : (
                            <BlockSettingsPanel
                                block={selectedBlock}
                                onUpdateStyles={(styles) => selectedBlock && updateBlockStyles(selectedBlock.id, styles)}
                                onUpdateContent={(content) => selectedBlock && updateBlockContent(selectedBlock.id, content)}
                                colors={colors}
                                fonts={fonts}
                            />
                        )}
                    </div>
                </div>
                )}
            </div>

            {/* ── Send Confirmation Modal ── */}
            {sendModalOpen && (
                <SendConfirmModal
                    recipientCount={recipientIDs?.length || 0}
                    subject={templateSubject}
                    templateName={templateName}
                    sending={sending}
                    onClose={() => setSendModalOpen(false)}
                    onSend={handleSend}
                    colors={colors}
                    fonts={fonts}
                />
            )}
        </div>
    );
}

// ─── Send Confirmation Modal ──────────────────────────────────────────────────
function SendConfirmModal({ recipientCount, subject, templateName, sending, onClose, onSend, colors, fonts }) {
    const [scheduleMode, setScheduleMode] = useState('now');
    const [scheduledFor, setScheduledFor] = useState(null);
    const isLater = scheduleMode === 'later';

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0,0,0,0.45)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 100,
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    backgroundColor: colors.surface,
                    borderRadius: 14,
                    padding: 28,
                    width: 480,
                    maxWidth: '90vw',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
                    fontFamily: fonts.body,
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                    <div style={{
                        width: 44,
                        height: 44,
                        borderRadius: 10,
                        backgroundColor: colors.primaryContainer || 'rgba(20,96,165,0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                        <Unicon name="paper-plane" library="fa" size={20} colorVariant="primary" />
                    </div>
                    <div>
                        <div style={{ fontSize: 18, fontWeight: '700', color: colors.text, fontFamily: fonts.heading }}>
                            Send Email
                        </div>
                        <div style={{ fontSize: 12, color: colors.textTertiary, marginTop: 2 }}>
                            Review before sending
                        </div>
                    </div>
                </div>

                <div style={{ backgroundColor: colors.background, borderRadius: 10, padding: 16, marginBottom: 20, border: `1px solid ${colors.border}` }}>
                    <Row label="Template" value={templateName} colors={colors} />
                    <Row label="Subject" value={subject || <em style={{ color: colors.textTertiary }}>No subject</em>} colors={colors} />
                    <Row label="Recipients" value={`${recipientCount} ${recipientCount === 1 ? 'person' : 'people'}`} colors={colors} last />
                </div>

                <div style={{ marginBottom: 20 }}>
                    <SchedulePicker
                        testID="email-send-schedule"
                        scheduleMode={scheduleMode}
                        scheduledFor={scheduledFor}
                        onScheduleChange={({ scheduleMode: mode, scheduledFor: date }) => {
                            if (mode) setScheduleMode(mode);
                            if (date !== undefined) setScheduledFor(date);
                        }}
                        nowLabel="Send Now"
                        laterLabel="Send Later"
                        pickerTitle="Send Email At"
                        colorVariant="primary"
                    />
                </div>

                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                    <button
                        onClick={onClose}
                        disabled={sending}
                        style={{
                            padding: '10px 18px',
                            backgroundColor: colors.background,
                            color: colors.text,
                            border: `1px solid ${colors.border}`,
                            borderRadius: 8,
                            fontSize: 14,
                            fontWeight: '500',
                            fontFamily: fonts.body,
                            cursor: sending ? 'not-allowed' : 'pointer',
                            opacity: sending ? 0.5 : 1,
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        data-testid="ConfirmSendEmail"
                        onClick={() => onSend({ scheduleMode, scheduledFor })}
                        disabled={sending || recipientCount === 0}
                        style={{
                            padding: '10px 22px',
                            backgroundColor: colors.primary,
                            color: '#fff',
                            border: 'none',
                            borderRadius: 8,
                            fontSize: 14,
                            fontWeight: '600',
                            fontFamily: fonts.body,
                            cursor: (sending || recipientCount === 0) ? 'not-allowed' : 'pointer',
                            opacity: (sending || recipientCount === 0) ? 0.5 : 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                        }}
                    >
                        {sending ? (isLater ? 'Scheduling...' : 'Sending...') : `${isLater ? 'Schedule for' : 'Send to'} ${recipientCount}`}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Row({ label, value, colors, last }) {
    return (
        <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 12,
            paddingBottom: last ? 0 : 10,
            marginBottom: last ? 0 : 10,
            borderBottom: last ? 'none' : `1px solid ${colors.border}`,
            fontSize: 13,
        }}>
            <span style={{ color: colors.textSecondary, fontWeight: '500' }}>{label}</span>
            <span style={{ color: colors.text, fontWeight: '600', textAlign: 'right' }}>{value}</span>
        </div>
    );
}

// ─── Template Chooser ─────────────────────────────────────────────────────────
function TemplateChooser({ recipientIDs, onPick }) {
    const { theme: { colors, fonts, shadows } } = useUnistyles();
    const { data: templates, isLoading } = Query.emailTemplates.all();
    const deleteTemplateMutation = Mutation.emailTemplates.delete();
    const [sortKey, setSortKey] = useState('updated-desc');
    const activeSort = TEMPLATE_SORT_OPTIONS.find(option => option.key === sortKey) || TEMPLATE_SORT_OPTIONS[0];
    const sortedTemplates = useMemo(() => {
        const list = [...(templates || [])];
        if (sortKey === 'name-asc' || sortKey === 'name-desc') {
            list.sort((a, b) => (a.name || 'Untitled template').localeCompare(b.name || 'Untitled template', undefined, { sensitivity: 'base' }));
            if (sortKey === 'name-desc') list.reverse();
        } else {
            list.sort((a, b) => new Date(a.updatedAt || a.createdAt || 0) - new Date(b.updatedAt || b.createdAt || 0));
            if (sortKey === 'updated-desc') list.reverse();
        }
        return list;
    }, [templates, sortKey]);

    const handleDelete = (tpl) => {
        showAlert({
            title: 'Delete template?',
            subtitle: `"${tpl.name || 'Untitled template'}" will be permanently archived.`,
            confirmText: 'Delete',
            isDestructive: true,
            onConfirm: () => deleteTemplateMutation.mutate(tpl.id),
        });
    };

    const cardBase = {
        backgroundColor: colors.surface,
        border: 'none',
        boxShadow: shadows.card,
        borderRadius: 12,
        padding: 20,
        cursor: 'pointer',
        textAlign: 'left',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        transition: 'all 0.15s ease',
        minHeight: 140,
        fontFamily: fonts.body,
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: colors.background }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '0 20px',
                height: 56,
                backgroundColor: colors.surface,
                borderBottom: `1px solid ${colors.border}`,
                flexShrink: 0,
            }}>
                <button onClick={() => router.back()} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                    <Unicon name="chevron-back" size={24} colorVariant="primary" />
                </button>
                <span style={{ fontSize: 16, fontWeight: '600', color: colors.text, fontFamily: fonts.heading, flex: 1 }}>
                    Email Builder
                </span>
                {recipientIDs?.length > 0 && (
                    <span style={{ fontSize: 13, color: colors.textSecondary, fontFamily: fonts.body }}>
                        Sending to {recipientIDs.length} {recipientIDs.length === 1 ? 'person' : 'people'}
                    </span>
                )}
                <DropdownMenu
                    testID="EmailTemplateSort"
                    align="end"
                    options={TEMPLATE_SORT_OPTIONS.map(option => ({
                        ...option,
                        selected: option.key === sortKey,
                        onSelect: () => setSortKey(option.key),
                    }))}
                    triggerComponent={(
                        <View style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 6,
                            paddingHorizontal: 10,
                            paddingVertical: 7,
                            borderRadius: 8,
                            backgroundColor: colors.background,
                        }}>
                            <Unicon name="swap-vertical-outline" size={15} colorVariant="textSecondary" />
                            <Text style={{ color: colors.textSecondary, fontFamily: fonts.body, fontSize: 12 }}>{activeSort.name}</Text>
                        </View>
                    )}
                />
                <SolidButton
                    testID="CreateEmailTemplate"
                    text="Create Template"
                    iconName="add"
                    iconBefore
                    type="primary"
                    size="sm"
                    onPress={() => onPick(null)}
                />
            </div>

            {/* Body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '40px 32px' }}>
                <div style={{ maxWidth: 960, margin: '0 auto' }}>
                    <h1 style={{ fontSize: 24, fontWeight: '700', color: colors.text, fontFamily: fonts.heading, marginBottom: 6 }}>
                        Choose a template
                    </h1>
                    <p style={{ fontSize: 14, color: colors.textSecondary, fontFamily: fonts.body, marginBottom: 28 }}>
                        Start from an existing template or create a brand new one.
                    </p>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                        gap: 16,
                    }}>
                        {/* Existing Templates */}
                        {isLoading ? (
                            <div style={{ color: colors.textSecondary, fontSize: 13 }}>Loading templates...</div>
                        ) : sortedTemplates.map((tpl) => (
                            <div
                                key={tpl.id}
                                role="button"
                                tabIndex={0}
                                onClick={() => onPick(tpl)}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPick(tpl); } }}
                                style={{ ...cardBase, position: 'relative' }}
                                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = shadows.cardHover || shadows.card; }}
                                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = shadows.card; }}
                            >
                                {/* Action buttons */}
                                <div
                                    onClick={(e) => e.stopPropagation()}
                                    style={{
                                        position: 'absolute',
                                        top: 10,
                                        right: 10,
                                        display: 'flex',
                                        gap: 4,
                                        zIndex: 5,
                                    }}
                                >
                                    <IconButton
                                        iconName="content-copy"
                                        iconLibrary="material"
                                        iconSize={16}
                                        buttonSize={30}
                                        type="textSecondary"
                                        tooltipText="Duplicate"
                                        onPress={() => onPick({
                                            id: null,
                                            name: `${tpl.name || 'Untitled template'} (Copy)`,
                                            subject: tpl.subject,
                                            design: tpl.design,
                                        })}
                                    />
                                    <IconButton
                                        iconName="trash"
                                        iconLibrary="ionicon"
                                        iconSize={16}
                                        buttonSize={30}
                                        type="error"
                                        tooltipText="Delete"
                                        onPress={() => handleDelete(tpl)}
                                    />
                                </div>
                                <div style={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 8,
                                    backgroundColor: colors.primaryContainer || 'rgba(20,96,165,0.1)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}>
                                    <Unicon name="mail" size={18} colorVariant="primary" />
                                </div>
                                <div style={{ fontSize: 15, fontWeight: '700', color: colors.text, fontFamily: fonts.heading, paddingRight: 40 }}>
                                    {tpl.name || 'Untitled template'}
                                </div>
                                {tpl.subject && (
                                    <div style={{ fontSize: 12, color: colors.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {tpl.subject}
                                    </div>
                                )}
                                <div style={{ marginTop: 'auto', fontSize: 11, color: colors.textTertiary }}>
                                    Updated {tpl.updatedAt ? new Date(tpl.updatedAt).toLocaleDateString() : '—'}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── Default Export: orchestrates picker and editor ───────────────────────────
export default function EmailEditorScreen() {
    const { isWeb } = usePlatform();
    const { theme: { colors } } = useUnistyles();
    const params = useLocalSearchParams();
    const initialRouteTemplateID = typeof params?.templateID === 'string' ? params.templateID : null;
    const [routeTemplateID, setRouteTemplateID] = useState(initialRouteTemplateID);
    const { data: routeTemplate, isLoading: loadingRouteTemplate } = Query.emailTemplates.byId({ id: routeTemplateID });

    useEffect(() => {
        setRouteTemplateID(initialRouteTemplateID);
    }, [initialRouteTemplateID]);

    const recipientIDs = (() => {
        try {
            return params?.recipientIDs ? JSON.parse(params.recipientIDs) : [];
        } catch { return []; }
    })();

    // null = picker mode. Once chosen: { id?, name?, subject?, design? } (null object = new blank template)
    const [picked, setPicked] = useState(undefined); // undefined = still in picker

    useEffect(() => {
        if (routeTemplateID && !loadingRouteTemplate && !routeTemplate) {
            Toast.show({ type: 'error', text2: 'Email template not found' });
            setRouteTemplateID(null);
        }
    }, [loadingRouteTemplate, routeTemplate, routeTemplateID]);

    if (!isWeb) {
        return (
            <View style={[styles.container, { backgroundColor: 'transparent' }]}>
                <Text style={[styles.message, { color: colors.text }]}>The email editor is available on the web app only.</Text>
            </View>
        );
    }

    if (routeTemplateID) {
        if (loadingRouteTemplate || !routeTemplate) {
            return (
                <View style={[styles.container, { backgroundColor: 'transparent' }]}>
                    <Text style={[styles.message, { color: colors.text }]}>Loading template...</Text>
                </View>
            );
        }
        return (
            <EmailEditorView
                key={routeTemplate.id}
                initialTemplate={routeTemplate}
                recipientIDs={recipientIDs}
                onChangeTemplate={() => {
                    setRouteTemplateID(null);
                    setPicked(undefined);
                }}
            />
        );
    }

    if (picked === undefined) {
        return <TemplateChooser recipientIDs={recipientIDs} onPick={setPicked} />;
    }

    return (
        <EmailEditorView
            key={picked?.id || 'new'}
            initialTemplate={picked}
            recipientIDs={recipientIDs}
            onChangeTemplate={() => setPicked(undefined)}
        />
    );
}

const styles = StyleSheet.create({
    container: { flex: 1, justifyContent: 'center', alignItems: 'center' },
    message: { fontSize: 16, textAlign: 'center' },
});
